import { createAsyncThunk } from '@reduxjs/toolkit';

import { fetchWrapper } from '../../../../helpers';
import { TREATMENT_PLANS_V2_API_BASE } from '../../constants';

import type { ITreatmentPlanV3CrisisPlan } from '../../../../pages/common';
import type {
  ITreatmentPlanV3CrisisPage,
  GetTreatmentPlanV3CrisisPagePayload,
  CreateTreatmentPlanV3CrisisPlanPayload,
  UpdateTreatmentPlanV3CrisisPlanPayload,
  DeleteTreatmentPlanV3CrisisPlanPayload,
  DeleteAllTreatmentPlanV3CrisisPlansPayload,
  DeleteAllTreatmentPlanV3CrisisPlansReject,
  DeleteAllTreatmentPlanV3CrisisPlansResult,
  SetTreatmentPlanV3NoCrisisPlanNeededPayload,
  TreatmentPlanV3CrisisThunkConfig,
} from './types';

function canMutateLoadedCrisisPage(
  { treatmentPlanId }: GetTreatmentPlanV3CrisisPagePayload,
  { getState }: { getState: () => TreatmentPlanV3CrisisThunkConfig['state'] },
) {
  const { crisisPage, crisisPageLoading, crisisPageTreatmentPlanId } = getState().treatmentPlanV3;
  return (
    crisisPage != null &&
    !crisisPageLoading &&
    crisisPageTreatmentPlanId === treatmentPlanId
  );
}

export function createTreatmentPlanV3CrisisThunks() {
  return {
    getTreatmentPlanV3CrisisPage: getTreatmentPlanV3CrisisPage(),
    createTreatmentPlanV3CrisisPlan: createTreatmentPlanV3CrisisPlan(),
    updateTreatmentPlanV3CrisisPlan: updateTreatmentPlanV3CrisisPlan(),
    deleteTreatmentPlanV3CrisisPlan: deleteTreatmentPlanV3CrisisPlan(),
    deleteAllTreatmentPlanV3CrisisPlans: deleteAllTreatmentPlanV3CrisisPlans(),
    setTreatmentPlanV3NoCrisisPlanNeeded: setTreatmentPlanV3NoCrisisPlanNeeded(),
  };
}

function shouldFetchCrisisPage(
  { treatmentPlanId, force }: GetTreatmentPlanV3CrisisPagePayload,
  { getState }: { getState: () => TreatmentPlanV3CrisisThunkConfig['state'] },
) {
  if (force) return true;
  const { crisisPage, crisisPageLoading, crisisPageTreatmentPlanId } = getState().treatmentPlanV3;
  if (crisisPageTreatmentPlanId !== treatmentPlanId) return true;
  return crisisPage == null && !crisisPageLoading;
}

function getTreatmentPlanV3CrisisPage() {
  return createAsyncThunk<
    ITreatmentPlanV3CrisisPage,
    GetTreatmentPlanV3CrisisPagePayload,
    TreatmentPlanV3CrisisThunkConfig
  >(
    'treatmentPlanV3/getTreatmentPlanV3CrisisPage',
    async ({ treatmentPlanId, getAccessTokenSilently }) =>
      fetchWrapper.get({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/crisis/`,
        getAccessTokenSilently,
      }),
    { condition: shouldFetchCrisisPage },
  );
}

function createTreatmentPlanV3CrisisPlan() {
  return createAsyncThunk<
    ITreatmentPlanV3CrisisPlan,
    CreateTreatmentPlanV3CrisisPlanPayload,
    TreatmentPlanV3CrisisThunkConfig
  >(
    'treatmentPlanV3/createTreatmentPlanV3CrisisPlan',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/crisis/`,
          getAccessTokenSilently,
        },
        data,
      ),
    { condition: canMutateLoadedCrisisPage },
  );
}

function updateTreatmentPlanV3CrisisPlan() {
  return createAsyncThunk<
    ITreatmentPlanV3CrisisPlan,
    UpdateTreatmentPlanV3CrisisPlanPayload,
    TreatmentPlanV3CrisisThunkConfig
  >(
    'treatmentPlanV3/updateTreatmentPlanV3CrisisPlan',
    async ({ treatmentPlanId, crisisPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/crisis/${crisisPlanId}/`,
          getAccessTokenSilently,
        },
        data,
      ),
    { condition: canMutateLoadedCrisisPage },
  );
}

function deleteTreatmentPlanV3CrisisPlan() {
  return createAsyncThunk<
    void,
    DeleteTreatmentPlanV3CrisisPlanPayload,
    TreatmentPlanV3CrisisThunkConfig
  >(
    'treatmentPlanV3/deleteTreatmentPlanV3CrisisPlan',
    async ({ treatmentPlanId, crisisPlanId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/crisis/${crisisPlanId}/`,
        getAccessTokenSilently,
      });
    },
    { condition: canMutateLoadedCrisisPage },
  );
}

function deleteAllTreatmentPlanV3CrisisPlans() {
  return createAsyncThunk<
    DeleteAllTreatmentPlanV3CrisisPlansResult,
    DeleteAllTreatmentPlanV3CrisisPlansPayload,
    TreatmentPlanV3CrisisThunkConfig & { rejectValue: DeleteAllTreatmentPlanV3CrisisPlansReject }
  >(
    'treatmentPlanV3/deleteAllTreatmentPlanV3CrisisPlans',
    async ({ treatmentPlanId, crisisPlanIds, getAccessTokenSilently }, { rejectWithValue }) => {
      const deletedIds: number[] = [];
      for (const crisisPlanId of crisisPlanIds) {
        try {
          await fetchWrapper.delete({
            url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/crisis/${crisisPlanId}/`,
            getAccessTokenSilently,
          });
          deletedIds.push(crisisPlanId);
        } catch {
          return rejectWithValue({
            deletedIds,
            message:
              deletedIds.length === 0
                ? 'Unable to delete crisis plans.'
                : `Deleted ${deletedIds.length} of ${crisisPlanIds.length} crisis plans. Try again to remove the rest.`,
          });
        }
      }
      return { deletedIds };
    },
    { condition: canMutateLoadedCrisisPage },
  );
}

function setTreatmentPlanV3NoCrisisPlanNeeded() {
  return createAsyncThunk<
    ITreatmentPlanV3CrisisPage,
    SetTreatmentPlanV3NoCrisisPlanNeededPayload,
    TreatmentPlanV3CrisisThunkConfig
  >(
    'treatmentPlanV3/setTreatmentPlanV3NoCrisisPlanNeeded',
    async ({ treatmentPlanId, noCrisisPlanNeeded, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/crisis/`,
          getAccessTokenSilently,
        },
        { no_crisis_plan_needed: noCrisisPlanNeeded },
      ),
    { condition: canMutateLoadedCrisisPage },
  );
}
